// FP 자원 간트 차트 — **프레임 자체 구현(AG Grid 미사용)**
//
// 전환 이유는 겹침 표시 요구다 — 한 자원 행의 활동이 겹칠 때 "한 줄에 겹쳐 그리기"와 "트랙으로
// 수직 나열"을 모두 지원해야 하고, 후자는 행 높이가 데이터에 따라 달라진다. AG Grid는 행 높이·
// 가상화·스크롤을 그리드가 소유하므로 이 요구와 이후 편집(드래그) 기능이 급격히 비싸진다
// (결정 근거: reports/gantt-implementation.md §7-1).
// 병행 유지했던 AG Grid 버전은 2026-08-19에 제거하고 이 파일이 정식 이름을 가져갔다(§7-3).
//
// 구성: 조회조건(Report 공통) + `GanttFrame`(스크롤·가상화·2단 헤더·좌측 pane·입력 창구) +
//       `ganttDraw`(캔버스 프리미티브) + `ganttRowModel`(행 높이·트랙 패킹).

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { withTranslation } from "react-i18next";
import { debounce } from "lodash";
import { format } from "date-fns";

import fpResourceGanttService from "@plannel/services/fp/fp-resource-gantt-service";
import fpCommonService from "@plannel/services/fp/fp-common-service";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import PeriodSearchGroup from "@plannel/pages/factory-plan/common/component/PeriodSearchGroup";
import GanttActivitySearch from "@plannel/pages/factory-plan/common/gantt/GanttActivitySearch";
import GanttColumnFilter from "@plannel/pages/factory-plan/common/gantt/GanttColumnFilter";
import GanttFrame from "@plannel/pages/factory-plan/common/gantt/GanttFrame";
import GanttDetailPanel from "@plannel/pages/factory-plan/common/gantt/GanttDetailPanel";
import GanttTooltip from "@plannel/pages/factory-plan/common/gantt/GanttTooltip";
import GanttViewControls from "@plannel/pages/factory-plan/common/gantt/GanttViewControls";
import ResourcePlanDetailTab from "@plannel/pages/factory-plan/report/resource-gantt/ResourcePlanDetailTab";
import ShortageDetailTab from "@plannel/pages/factory-plan/report/resource-gantt/ShortageDetailTab";
import PsiTab from "@plannel/pages/factory-plan/report/resource-gantt/PsiTab";
import { buildRowLayout, markAbutment, OVERLAP_MODE } from "@plannel/pages/factory-plan/common/gantt/ganttRowModel";
import {
  drawBox,
  drawBoxLabel,
  drawDependency,
  drawDivideMarks,
  drawDowntimes,
  drawSelectionRing,
  drawFrozenZone,
  drawRowDivider,
  drawTimelineGrid,
  drawWeekends
} from "@plannel/pages/factory-plan/common/gantt/ganttDraw";
import { buildGanttBound, buildTicks, buildTimeScale, buildWeekendRanges, toMs } from "@plannel/pages/factory-plan/common/gantt/timelineScales";
import { formatCodeName, formatQty, formatTimeDiff } from "@plannel/pages/factory-plan/common/gantt/ganttFormat";
import {
  DEFAULT_HEADER_UNIT,
  LANE_BAR_PADDING_Y,
  ROW_HEIGHT_DEFAULT,
  UI,
  WIDTH_ZOOM_MAX,
  WIDTH_ZOOM_MIN,
  WIDTH_ZOOM_STEP
} from "@plannel/pages/factory-plan/common/gantt/ganttConstants";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import numberUtil from "@plannel/utils/number-util";
import dateUtils from "@plannel/utils/date-util";
import debug from "@plannel/utils/debug-util";

const { snackBarMessageColor } = ColorUtil;

// old FP_SRH_RANGE.attr01Val(DB 단일 고정값) 이식 — 다른 Report 화면과 동일 결정(§A-3c).
// TODO(MIG-T2): [resourcegantt/ResourceGantt] 조회조건 세팅 3종(버킷 목록/검색필드 표시/검색구간)의
//   테넌트별 커스터마이징 미이관.
//   old: setting FP_SRH_RANGE / search-option 조회로 planScope별 설정을 읽음
//   사유: 정적 구성(상수/고정 카탈로그)으로 전환 — fp-report-screen-guide.md §A-3~§A-3d에서 결정
const SEARCH_ZONE_DAYS = 9;

const PARAM_DATE_FORMAT = "yyyyMMdd";
const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

// old ActivitySearch(팝오버 안의 작업주문/품목 입력 2개) 대응. **서버 조회 조건이 아니라 화면 안의
// 강조/흐리기 필터**라 커밋해도 재조회하지 않고 캔버스만 다시 그린다.
// 순서가 곧 팝오버의 입력 순서다(old: 주문 → 제품).
const SEARCH_FIELDS = [
  { key: "order", labelKey: "FP_WO_CD" },
  { key: "item", labelKey: "FP_ITEM" }
];

// 수직 나열(STACK) 모드에서 트랙 1개가 쓰는 높이. 겹침이 2트랙이면 행이 그만큼 높아진다.
const TRACK_HEIGHT = 20;

// 전역 휴일 밴드(`drawHolidays` + 빈 `HOLIDAY_RANGES`)는 **제거했다**(2026-08-24).
// 휴일 설정 결과가 자원 비가동(`tb_fp_resrc_downtime`)으로 저장되면서 **`drawDowntimes`가 자원 행별로
// 이미 그린다**(§7-19·§7-20a) — 전 행을 관통하는 별도 캘린더 레이어는 중복이고, 자원마다 휴무가
// 다를 수 있으니 오히려 행별 표시가 정확하다. 2026-08-19의 TODO(MIG-T2)는 이 결정으로 해소.

// 아래 파서/판정 4개는 AG Grid 버전과 복제 상태였는데, 그쪽이 제거되면서(2026-08-19, §7-3)
// 이 파일에만 남았다. 쓰는 화면이 하나뿐이라 `common/gantt/`로 승격하지 않고 여기 둔다 —
// AdjustmentGantt가 같은 파싱을 필요로 하면 그때 옮긴다.
const checkTrue = (value) => value === true || value === "true";

// old checkSearchActivity 이식 — 품목(코드/명칭) 부분일치 AND 작업주문 부분일치. 빈 조건은 통과.
const matchActivity = (search, activity) => {
  if (!activity) return true;
  const item = String(search?.item ?? "").toUpperCase();
  const order = String(search?.order ?? "").toUpperCase();
  if (!item && !order) return true;

  const inventoryCd = String(activity.inventoryCd ?? "").toUpperCase();
  const inventoryNm = String(activity.inventoryNm ?? "").toUpperCase();
  const woCd = String(activity.woCd ?? "").toUpperCase();

  return (!item || inventoryCd.includes(item) || inventoryNm.includes(item)) && (!order || woCd.includes(order));
};

// `"MM/dd/yyyy HH:mm~MM/dd/yyyy HH:mm#20;"` 반복 → 구간 배열. 클래스 번호(#20)는 old TreeGrid의
// CSS 클래스 체계라 캔버스에서는 쓰지 않는다(§5-2a F5).
// 비가동 밴드 구간 — 백엔드가 `GanttDowntimeDto{startTs, endTs}` 목록으로 내려준다(§7-19).
//
// 예전에는 `"MM/dd/yyyy HH:mm~…#20;"` 문자열을 `date-fns parse`로 풀었는데, `parse`는 값을 **로컬**로
// 해석하고 활동·축 시각은 `toMs`가 `"…Z"`를 **UTC**로 해석해서 **밴드가 로컬 오프셋만큼 어긋났다**
// (2026-08-24 사용자 지적). 이제 활동 시각과 **같은 `toMs` 경로**를 타므로 기준이 하나다.
const toRanges = (list) => (list ?? [])
  .map(downtime => ({ s: toMs(downtime.startTs), e: toMs(downtime.endTs) }))
  .filter(range => !Number.isNaN(range.s) && !Number.isNaN(range.e) && range.e > range.s);

// 작업 교체(JC) 박스는 백엔드가 실제 활동 앞뒤로 끼워 넣은 합성 활동이고, **`uniqueId` 접미사로만**
// 어느 쪽인지 알 수 있다(`ResourceGanttService.java:33-34`의 `_PREVJC` / `_NEXTJC`).
// `displayColor`(#000000 / #808080)로도 갈릴 수 있지만 색은 표현이고 접미사가 의도다 — 색 토큰을
// 바꿔도 이 판정이 깨지지 않도록 접미사를 본다.
const NEXT_JC_SUFFIX = "_NEXTJC";

const jcKindOf = (activity) => (
  String(activity?.uniqueId ?? "").endsWith(NEXT_JC_SUFFIX) ? "next" : "prev"
);

// JC 박스 id에서 딸린 작업의 id를 되돌린다 — 백엔드가 `uniqueId + "_PREVJC"/"_NEXTJC"`로 만든다.
// `uniqueId`는 `ActivityDto`에서 String이라 접미사 비교가 그대로 성립한다.
const jcParentIdOf = (boxId) => String(boxId ?? "").replace(/_(PREV|NEXT)JC$/, "");

// 선택 링 하나가 덮을 사각형 — **가로는 작업 바 + 앞뒤 JC 띠의 합집합, 세로는 작업 바 그대로**.
//
// 처음엔 합집합의 실제 외곽선(JC 구간에서 절반 높이로 내려가는 계단 모양)을 그렸는데,
// **JC 없는 박스의 둥근 링과 모양이 달라 이질적이었다**(2026-08-24 사용자 지적, §7-18a).
// 링은 "무엇이 선택됐나"만 말하면 되고 그 안의 두께 차이는 JC 띠 자체가 이미 말한다 →
// 링은 항상 같은 둥근 사각형 하나로 두고, 가로 범위만 JC까지 넓힌다.
//
// 작업 바가 가로 클리핑으로 화면 밖이면(JC만 보이는 경우) JC 사각형들의 세로 범위로 폴백한다.
const selectionRect = (barRect, jcRects) => {
  const parts = [barRect, ...jcRects].filter(Boolean);
  if (parts.length === 0) return null;

  const left = Math.min(...parts.map(p => p.left));
  const right = Math.max(...parts.map(p => p.left + p.width));
  const top = barRect ? barRect.top : Math.min(...parts.map(p => p.top));
  const bottom = barRect
    ? barRect.top + barRect.height
    : Math.max(...parts.map(p => p.top + p.height));

  return { left, top, width: right - left, height: bottom - top };
};

// old activityToRunBox 이식 — 활동 배열 → 캔버스가 그릴 박스 배열.
// `startTs` 오름차순 정렬은 히트테스트와 트랙 패킹(packTracks)의 전제다.
const buildBoxes = (activities) => (activities ?? [])
  .map(activity => {
    const s = toMs(activity.startTs);
    const e = toMs(activity.endTs);
    if (Number.isNaN(s)) return null;

    const jc = checkTrue(activity.jcYn);
    const shtg = checkTrue(activity.shtgYn);

    return {
      id: activity.uniqueId,
      s,
      e: Number.isNaN(e) ? s : e,
      type: shtg ? "shtg" : jc ? "jc" : "box",
      // JC는 두께(절반)와 색(prev/next)이 다르다 — §7-17. 결품이면 type이 "shtg"로 이겨서
      // 마름모가 되므로 그때는 jcKind가 쓰이지 않는다.
      jcKind: jc ? jcKindOf(activity) : null,
      color: activity.displayColor,
      text: activity.woCd,
      late: checkTrue(activity.lateYn),
      hasTool: (activity.toolUsage?.length ?? 0) > 0,
      activity
    };
  })
  .filter(Boolean)
  .sort((a, b) => a.s - b.s);

// 정렬 직후 인접(맞닿음)을 표시한다 — `drawBox`가 맞닿은 쪽 코너를 각지게 그린다(§7-28).
// **여기가 맞는 자리다**: 인접은 시각(ms)의 성질이라 줌·행높이가 바뀌어도 다시 계산할 필요가 없다.
const buildLaneBoxes = (...args) => markAbutment(buildBoxes(...args));

// activity-relations 응답 파싱 — `Map<woCd, "행,행,from,to/…">`. 앞 2필드(TreeGrid 행 인덱스)는
// 우리 행 정렬과 어긋나므로 버리고 uniqueId 2개만 쓴다(§5-4-(1)).
const parseRelations = (value) => String(value ?? "")
  .split("/")
  .filter(Boolean)
  .map(segment => {
    const parts = segment.split(",");
    return parts.length >= 4 ? { from: parts[2], to: parts[3] } : null;
  })
  .filter(Boolean);

// ── 하단 상세탭 영역 (§7-14) ────────────────────────────────────────────────
// 레이아웃 = **old `DetailsSplitArea` 구성 그대로**: 간트 → 분할 바 → [탭 바 + 탭 내용].
// 탭 바가 분할 바 **아래**(하단 영역 안)에 있고, "접힘"은 하단 영역이 **탭 바 높이만 남는 것**이다
// (화살표 + 탭 라벨만 보이는 띠 = old 초기 97/3과 같은 모양).
//   ※ 검토 단계에서는 탭 바를 상단 영역으로 올리는 "안 1"을 골랐었다 — 공용 `SplitterContainer`의
//     접힘 높이가 8px 고정(`COLLAPSED_SIZE`)이라 탭 바를 하단에 두면 접힘 상태에서 탭이 사라지기
//     때문이었다. 아래 이유로 그 컴포넌트를 결국 쓰지 못하고 분할을 직접 소유하게 되면서 그 제약이
//     사라졌으므로, **old 모양으로 되돌렸다**(2026-08-24 사용자 지시). 시안 비교는
//     prototypes/gantt-detail-tabs-layout.html(안 1 기준으로 만든 문서라 탭 바 위치만 다르다).
//
// ⚠️ **공용 `components/SplitterContainer.js`를 쓰지 않고 이 화면 안에서 분할을 직접 소유한다.**
//    확정 요구(① 초기 접힘 ② 화살표로 지정 높이 펼침 ③ 드래그 높이로 접힘/펼침 자동 판정)를
//    그 컴포넌트로는 만들 수 없다는 것을 소스로 확인했다(react-collapse-pane 3.0.1):
//      - `sizes`(useSplitPaneResize.ts:78)와 `collapsedIndices`(SplitPane/index.tsx:74)가 내부
//        `useState`이고 **초기값만** prop으로 받는다 → controlled API가 없다(②가 불가).
//      - `useHandleDragFinished`는 `setSizes`만 부르고 `collapsedIndices`를 갱신하지 않는다
//        → 드래그로 끝까지 줄여도 "접힘"으로 인식되지 않고 화살표가 반전되지 않는다(③이 불가).
//      - 토글 버튼은 `Resizer/index.tsx:126`에서 split bar 안에만 렌더된다 → 탭 바 왼쪽에 못 둔다.
//      - **결정적** — `SplitterContainer.js:36-44`의 `hooks.onCollapse`가 라이브러리의
//        `collapsedSizes` **상태 배열을 in-place로 +42 mutate**하는데, 라이브러리가 그 훅을
//        `useEffect(…, [collapsedSizes, hooks])`로 부르고 `hooks`는 SplitterContainer가 매 렌더
//        새로 만드는 객체 리터럴이라 **리렌더마다 +42가 누적된다**. 이 화면은 hover·툴팁·선택마다
//        리렌더되므로 복원 높이가 무한히 커져 펼치는 순간 간트가 최소 높이(8px)로 짜부라진다.
//        (`useSplitPaneResize.ts:80`이 prop 배열 **참조 그대로** 상태로 쓰므로 ①도 같이 오염된다.)
//    공용 컴포넌트 수정은 CLAUDE.md 금지 사항(FP 21화면 + DP 3화면 공유)이라 손대지 않았다.
//    controlled `SplitterContainer`로 승격하는 안은 사람 결정 대기 — reports/pending-decisions.md.
const DETAIL_TABS = [
  { key: "plan", labelKey: "FP_RESOURCE_PLAN_DETAILS" },
  { key: "shortage", labelKey: "PROD_PRBM" },
  { key: "psi", labelKey: "PSI" }
];
const DETAIL_TAB_BAR_HEIGHT = 38;    // 탭 바 높이. 접힘 = 하단 영역이 이 높이만 남은 상태다
// **화살표로 펼칠 때는 항상 이 높이로 간다** — old `handleDrawerOpen`이 직전 드래그 결과와 무관하게
// 늘 `setSplitSizes([70, 30])`을 넣었던 것과 같다(2026-08-24 사용자 확인). 드래그로 조절한 높이를
// 복원하지 않는다: "화살표는 늘 같은 크기로 연다"가 예측 가능해서 old가 그렇게 만든 것이다.
const DETAIL_OPEN_HEIGHT = 340;      // 탭 바 + 여백 포함 높이(내용 ≈ 296px = 2단 헤더 + 약 8행)
// 드래그 하한은 탭 바 높이(위 `DETAIL_TAB_BAR_HEIGHT`)다 — 탭 바를 뭉갤 수 없다는 뜻이고,
// old `DetailsSplitArea`의 `minSize={[0, 45]}`(하단 최소 45px)와 같은 역할이다.
const DETAIL_MAX_OPEN = 720;         // 상한 — 간트가 최소한 남도록
const DETAIL_GUTTER = 10;            // 분할 바 두께. SplitterContainer 기본값과 같게 맞춘다
const DETAIL_GUTTER_MARGIN = 4;      // 분할 바 위(간트)·아래(탭 바) 여백
// 탭 바와 그리드 사이 여백. **접힘 상태에서는 적용하지 않는다** — 접히면 하단 영역이 탭 바 높이만
// 남는데 거기에 여백까지 더하면 합계가 `detailHeight`를 넘겨 탭 바가 잘린다.
const DETAIL_CONTENT_GAP = 6;
// 드래그를 놓았을 때 탭 바 높이 + 이 값 이하면 접힘으로 스냅한다(old handleSplitDragEnd의 45px 임계 이식).
const DETAIL_COLLAPSE_THRESHOLD = DETAIL_TAB_BAR_HEIGHT + 45;

// old `common/utils/gantt.js:530 getGanttRowData` 이식 — 선택한 자원 행의 활동 목록.
// old는 TreeGrid RunBox의 `activityData`(JSON 문자열)를 역파싱했지만, new는 `buildBoxes`가 full
// `ActivityDto`를 `box.activity`에 그대로 들고 있어 그걸 쓰면 된다(그래서 **API 호출이 없다**).
// old와 같이 준비시간(jcYn) 활동은 제외한다 — `box.type`이 아니라 `jcYn`을 직접 본다
// (`buildBoxes`는 결품이면 jc여도 type을 "shtg"로 주므로 type으로 걸러내면 old와 어긋난다).
const buildResourcePlanRows = (row) => (row?.boxes ?? [])
  .filter(box => !checkTrue(box.activity?.jcYn))
  .map(box => ({
    ...box.activity,
    uniqueId: box.id,
    plantCd: row.plantCd,
    plantNm: row.plantNm,
    resourceCd: row.resourceCd,
    resourceNm: row.resourceNm,
    lateYn: box.late
  }));

const ResourceGantt = ({ t, viewName }) => {
  const [versionState, setVersionState] = useState({
    planScope: "", planningDate: null, versionCd: "", plantCd: [], selectedVersion: null
  });
  const [periodState, setPeriodState] = useState({
    startDate: null, endDate: null, bucketType: "", isEntirePlanPeriod: true
  });
  // selected = **커밋된** 조회 조건(이게 바뀌면 자동 조회가 나간다), options = 드롭다운 목록.
  const [resourceState, setResourceState] = useState({ selected: [], options: [] });
  // 드롭다운을 열어 편집하는 동안의 임시 선택(옵션 객체 배열). null = 편집 중 아님.
  // 체크 하나마다 조회가 나가면 안 되므로 **닫을 때 1회만** selected로 커밋한다
  // (같은 관례: `simulation/AdjustmentGrid.js`의 자원 멀티셀렉트).
  const [resourceDraft, setResourceDraft] = useState(null);

  // 검색값은 **커밋된 것만** state로 둔다 — 입력 중 값은 `GanttActivitySearch`가 자기 안에서 들고 있다
  // (키 입력마다 캔버스를 다시 그리면 타이핑하는 동안 화면이 계속 흐려졌다 밝아진다).
  const [search, setSearch] = useState({ order: "", item: "" });

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [loading, setLoading] = useState(false);
  const [rawRows, setRawRows] = useState([]);
  const [frozenZone, setFrozenZone] = useState(null);
  const [bound, setBound] = useState(null);
  const [edges, setEdges] = useState([]);
  // 더블클릭으로 의존선을 띄운 작업주문. **그 주문의 작업만 남기고 나머지를 흐린다** — 검색 강조와
  // 같은 장치(`LANE_DIM_ALPHA`)를 그대로 쓴다(§7-24). 의존선만 그리면 선이 지나가는 자원 행에
  // 남의 작업 수십 개가 같은 밝기로 널려 있어 "선이 어디서 어디로 갔는지"를 눈으로 못 따라간다.
  const [focusWoCd, setFocusWoCd] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [hoverId, setHoverId] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [altHighlight, setAltHighlight] = useState(() => new Set());

  const [headerUnit, setHeaderUnit] = useState(DEFAULT_HEADER_UNIT);
  const [widthZoom, setWidthZoom] = useState(1);
  const [rowHeight, setRowHeight] = useState(ROW_HEIGHT_DEFAULT);
  // 겹침 표시 방식. **화면 토글은 두지 않는다**(2026-08-19 사용자 지시로 제거) — old TreeGrid도
  // `GanttRunError:'Merge'` + `GanttRunErrorsShift:''`로 "한 자원 = 한 트랙, 겹치면 그대로 겹쳐 그림"
  // 하나뿐이었다(§5-2a D3). 수직 나열(STACK)은 렌더·행모델 쪽에 남겨 뒀으니 이 값만 바꾸면 살아난다.
  const overlapMode = OVERLAP_MODE.OVERLAY;

  const relationsRef = useRef({});
  const altResourcesRef = useRef({});
  const frameApiRef = useRef(null);
  // boxId → 마지막으로 그린 화면 좌표. 의존선이 좌표를 되찾는 통로 — **우리가 그린 좌표를 그대로
  // 쓰기 때문에** AG Grid 시절의 좌표 재계산 오차(§5-1a B4·B13~B15)가 원천적으로 없다.
  const boxRectsRef = useRef(new Map());
  // 선택된 바가 **실제로 그려진 정수 사각형** — 행 렌더에서 채우고 overlay에서 링을 그린다.
  // `geom`(소수)을 링에서 다시 반올림하면 바와 1px 어긋난다(§7-9) → 바가 반환한 값을 그대로 쓴다.
  const selectedRectRef = useRef(null);
  // 선택된 작업에 **딸린 JC 띠**의 사각형들(앞/뒤 최대 2개). 링을 작업 바 하나가 아니라
  // JC까지 포함한 합집합으로 그리기 위해 모은다(§7-18).
  const selectedJcRectsRef = useRef([]);
  const hoverTimerRef = useRef(0);
  const tooltipTokenRef = useRef(0);

  // 우클릭 컨텍스트 메뉴 + 상세 패널(§7-7). old는 메뉴 항목이 1개(`FP_ACTIVITY_DETAIL`)였고
  // 준비시간(jcYn) 박스에서는 메뉴 자체를 막았다 — 그 규칙을 그대로 지킨다.
  const [menuAnchor, setMenuAnchor] = useState(null);        // { x, y } — 커서 위치(가상 앵커)
  const [menuTarget, setMenuTarget] = useState(null);        // { box, row }
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailSections, setDetailSections] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [openDetailSections, setOpenDetailSections] = useState(() => new Set(["activity", "order"]));
  const detailTokenRef = useRef(0);
  // 좌측 pane 셀 툴팁이 지금 어느 행에 떠 있는지(자원명 툴팁의 재설정 억제용).
  const leftTipKeyRef = useRef(null);
  const latestReqRef = useRef(0);
  const latestResourceReqRef = useRef(0);

  const searchStateRef = useRef({ versionState, periodState, resourceState });
  searchStateRef.current = { versionState, periodState, resourceState };

  const weekdayLabels = useMemo(() => WEEKDAY_KEYS.map(key => t(key)), [t]);
  const searchActive = Boolean(search.order || search.item);
  // 흐리기 판정 하나로 두 기능(검색 / 의존선 포커스)을 돌린다 — 그려지는 쪽(`drawBox`,
  // `drawDivideMarks`)은 "누가 켰는지"를 알 필요가 없다. **포커스가 검색을 이긴다**: 검색 중에
  // 더블클릭하면 그 주문 하나로 좁히는 것이 사용자가 방금 한 동작의 의도다.
  const matchFn = useCallback(
    (activity) => (focusWoCd ? activity?.woCd === focusWoCd : matchActivity(search, activity)),
    [search, focusWoCd]
  );

  const [matchIndex, setMatchIndex] = useState(0);

  // ── 시간축 / 행 레이아웃 ──────────────────────────────────────────────────
  const scale = useMemo(
    () => (bound ? buildTimeScale({ startTs: bound.startTs, endTs: bound.endTs, unit: headerUnit, zoom: widthZoom }) : null),
    [bound, headerUnit, widthZoom]
  );
  const ticks = useMemo(() => buildTicks(scale, { weekdayLabels }), [scale, weekdayLabels]);
  // 주말 밴드는 축(t0/t1)만 바뀔 때 다시 계산하면 된다 — 버킷·줌이 바뀌면 scale이 새로 만들어진다.
  const weekendRanges = useMemo(() => buildWeekendRanges(scale), [scale]);
  // ── 자원 컬럼 필터 (§7-13) ────────────────────────────────────────────────
  // **조회 결과 안에서 표시 행을 좁히는 클라이언트 필터**다(서버 재조회 없음). 상단 조회조건의 자원
  // 멀티셀렉트와 역할이 갈린다 — 그쪽은 "무엇을 가져올까", 이쪽은 "가져온 것 중 무엇을 볼까".
  // 빈 Set = 필터 없음.
  const [resourceFilter, setResourceFilter] = useState(() => new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);

  // ── 하단 상세탭 (§7-14) ──────────────────────────────────────────────────
  // **확정: 초기 = 접힘.** 접힘이란 하단 영역이 **탭 바 높이만 남는 것**이다(내용은 감춰지고
  // 화살표 + 탭 라벨만 보인다) — old의 초기 97/3과 같은 모양이다.
  const [detailTab, setDetailTab] = useState(DETAIL_TABS[0].key);
  const [detailHeight, setDetailHeight] = useState(DETAIL_TAB_BAR_HEIGHT);
  const detailDragRef = useRef(null);
  const detailCollapsed = detailHeight < DETAIL_COLLAPSE_THRESHOLD;
  // 생산 차질은 조회 범위 전체 목록이라 바 선택과 무관하다 — 조회 시 1회만 받는다(old와 동일).
  const [shortageRows, setShortageRows] = useState([]);

  // 값 목록 = **조회된 행의 distinct 자원**(old Defaults의 `*RowsAll`과 같은 의미).
  // planScope 전체 목록(`resourceState.options`)을 쓰면 화면에 없는 자원까지 뜬다.
  const resourceFilterOptions = useMemo(() => {
    const seen = new Map();
    rawRows.forEach(row => {
      if (!seen.has(row.resourceCd)) seen.set(row.resourceCd, row.resourceCd);
    });
    return [...seen.values()].map(value => ({ value, label: value }));
  }, [rawRows]);

  // **조회하면 필터를 비운다** — old도 매 조회마다 `ChangeFilter(...,'0')`로 초기화했다. 없으면 새
  // 데이터에 없는 자원 코드로 필터가 남아 화면이 빈다.
  useEffect(() => {
    setResourceFilter(prev => (prev.size === 0 ? prev : new Set()));
  }, [rawRows]);

  const filteredRows = useMemo(
    () => (resourceFilter.size === 0 ? rawRows : rawRows.filter(row => resourceFilter.has(row.resourceCd))),
    [rawRows, resourceFilter]
  );

  // 필터로 행이 줄면 스크롤이 범위를 벗어나 빈 영역이 보인다 → 맨 위로 되돌린다.
  useEffect(() => {
    frameApiRef.current?.scrollTo({ top: 0 });
  }, [resourceFilter]);

  // ── 하단 분할 조작 (§7-14) ────────────────────────────────────────────────
  // 화살표 = 접기/펼치기. **접어도 선택은 유지한다**(상세 패널과 같은 규칙 — 어느 바를 보던
  // 중이었는지 남아야 한다).
  const toggleDetail = useCallback(() => {
    setDetailHeight(prev => (
      // 펼칠 때는 **직전 드래그 높이를 복원하지 않고 늘 `DETAIL_OPEN_HEIGHT`로 간다**(old 동작).
      prev >= DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : DETAIL_OPEN_HEIGHT
    ));
  }, []);

  // 분할 바 드래그 — 위로 끌면 커지므로 부호가 반대다. 놓는 순간 높이로 접힘/펼침을 **자동 판정**해
  // 화살표 모양이 따라온다(old handleSplitDragEnd의 45px 임계 판정 이식).
  const handleDetailDragStart = useCallback((event) => {
    event.preventDefault();
    detailDragRef.current = { y: event.clientY, height: detailHeight };

    const onMove = (moveEvent) => {
      const drag = detailDragRef.current;
      if (!drag) return;
      // 탭 바는 항상 남으므로 하한이 0이 아니라 탭 바 높이다. 상한은 간트가 최소한 남도록 둔다
      // (컨테이너 높이를 모르니 보수적으로 고정값을 쓴다).
      const next = Math.max(
        DETAIL_TAB_BAR_HEIGHT,
        Math.min(DETAIL_MAX_OPEN, drag.height - (moveEvent.clientY - drag.y))
      );
      setDetailHeight(next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      detailDragRef.current = null;
      // 임계 미만에서 놓으면 접힘으로 스냅한다. 그 이상이면 놓은 높이를 그대로 쓴다 — 화살표는
      // 이 값을 복원하지 않고 늘 `DETAIL_OPEN_HEIGHT`로 열므로 따로 기억해 둘 필요가 없다.
      setDetailHeight(prev => (prev < DETAIL_COLLAPSE_THRESHOLD ? DETAIL_TAB_BAR_HEIGHT : prev));
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [detailHeight]);

  // 선택한 자원 행의 활동 목록 = "생산 계획 상세" 탭 데이터. **`rawRows`를 쓴다**(layout이 아니라) —
  // 자원 컬럼 필터로 화면에서 감춰진 행이라도 선택이 남아 있으면 그 행의 상세는 보여야 한다.
  const resourcePlanRows = useMemo(() => {
    if (!selectedRowKey) return [];
    const row = rawRows.find(candidate => candidate.rowKey === selectedRowKey);
    return row ? buildResourcePlanRows(row) : [];
  }, [rawRows, selectedRowKey]);

  // PSI 탭이 조회에 쓰는 선택 활동(plantCd/itemCd). 선택이 없으면 null → 탭이 빈 채로 남는다.
  const selectedActivity = useMemo(() => {
    if (!selectedId) return null;
    for (const row of rawRows) {
      const box = (row.boxes ?? []).find(candidate => candidate.id === selectedId);
      if (box) return box.activity;
    }
    return null;
  }, [rawRows, selectedId]);

  // **필터를 거친 행으로 레이아웃을 만든다** — 이 한 곳만 바뀌면 병합 셀·의존선·검색 건수·⌃⌄ 이동이
  // 전부 따라온다(모두 `layout`을 출처로 쓴다).
  const layout = useMemo(
    () => buildRowLayout(filteredRows, { overlapMode, baseRowHeight: rowHeight, trackHeight: TRACK_HEIGHT }),
    [filteredRows, overlapMode, rowHeight]
  );

  // 일치 작업 목록(시간순) — 건수 배지 · ⌃⌄ 이동 · 좌측 pane 표식이 모두 이걸 쓴다(§7-8).
  // **`layout.rows`를 훑는다**(rawRows가 아니라) — 이동할 때 x(시각)뿐 아니라 y(`row.top`)가 필요하고,
  // 그 좌표는 행 레이아웃이 계산한 값이다.
  const matches = useMemo(() => {
    if (!searchActive) return [];

    const hits = [];
    layout.rows.forEach(row => (row.boxes ?? []).forEach(box => {
      if (box.type !== "jc" && matchActivity(search, box.activity)) hits.push({ box, row });
    }));
    return hits.sort((a, b) => a.box.s - b.box.s);
  }, [layout, search, searchActive]);
  // box.id → { row, box } (**전 행**). 의존선이 화면 밖 행의 끝점 좌표를 계산할 때 쓴다(§7-12).
  // `layout.rows`를 쓰는 이유는 `row.top`이 필요해서다 — 그 좌표는 행 레이아웃이 계산한 값이다.
  const boxIndex = useMemo(() => {
    const map = new Map();
    layout.rows.forEach(row => (row.boxes ?? []).forEach(box => {
      if (box.id) map.set(box.id, { row, box });
    }));
    return map;
  }, [layout]);

  // 일치가 있는 자원 행 — 좌측 pane에서 "어디를 봐야 하는지" 알려준다(행이 수백 개다).
  const matchedResources = useMemo(
    () => new Set(matches.map(hit => hit.row.resourceCd)),
    [matches]
  );
  // 흐리기를 실제로 걸지 여부 — 검색이 걸렸고 **일치가 1건 이상일 때만**(§7-8).
  // 의존선 포커스는 0건이 될 수 없다(더블클릭한 바 자신이 항상 일치) → 건수 검사가 필요 없다.
  const dimming = Boolean(focusWoCd) || (searchActive && matches.length > 0);

  // 검색 일치 작업주문들의 의존선. 검색 커밋 시점과 **의존선 포커스를 푼 뒤 검색 상태로 되돌릴 때**
  // 같은 값이 필요해서 따로 뺐다.
  const buildSearchEdges = useCallback((target) => {
    if (!target.order && !target.item) return [];

    const woCds = new Set();
    rawRows.forEach(row => row.boxes.forEach(box => {
      if (box.type !== "jc" && matchActivity(target, box.activity) && box.activity?.woCd) woCds.add(box.activity.woCd);
    }));

    const list = [];
    woCds.forEach(woCd => list.push(...parseRelations(relationsRef.current?.[woCd])));
    return list;
  }, [rawRows]);

  // TODO(MIG-T2): [resourcegantt/ResourceGantt] 좌측 컬럼 정렬 / 컬럼 상태 저장 미이관(폭 조절은 구현됨).
  //   old(AG Grid 버전): `sortable` + `GridUtils.setColumnDefs`의 컬럼 상태 저장/복원
  //   사유: 자체 프레임 전환으로 상실 — 간트는 행 순서가 곧 자원 순서라 정렬 수요가 낮다(§7-1)
  const columns = useMemo(() => [
    // 공장 세로 병합 — old `Spanned:'1'`(AG Grid 버전은 `enableCellSpan` + `spanRows`)과 같은 결과.
    { key: "plantNm", label: t("FP_PLANT"), width: 140, spanRows: true, valueGetter: (row) => row.plantNm ?? row.plantCd ?? "" },
    {
      key: "resourceCd", label: t("FP_RESOURCE"), width: 200, valueGetter: (row) => row.resourceCd ?? "",
      // 헤더 깔때기 아이콘 — **자원 컬럼에만** 준다(old도 `resourceCd`만 `CanFilter:"1"`이었고,
      // 공장 컬럼은 병합 셀이라 값 필터가 어색하다).
      filterable: true,
      filterActive: resourceFilter.size > 0,
      // 세 가지 강조가 한 셀에 겹칠 수 있어 **채널을 전부 다르게** 쓴다(§7-10):
      //   대체자원 = 무채색 면 + 굵기 / 검색 일치 = 좌측 3px teal 레일 / 선택 행 = 행 배경 teal 12%
      // 색을 공유하면 어느 쪽 강조인지 구분이 안 된다 — 대체자원만 색을 안 쓰는 이유는
      // `UI.altResourceBg` 주석 참고(teal은 상호작용·상태 전용).
      cellStyle: (row) => ({
        ...(altHighlight.has(row.resourceCd) ? { backgroundColor: UI.altResourceBg, fontWeight: 700 } : null),
        ...(matchedResources.has(row.resourceCd) ? { boxShadow: `inset 3px 0 0 ${UI.headerAccent}` } : null)
      })
    },
    // 자원명 컬럼은 두지 않는다 — old와 같이 **코드 셀 hover 툴팁**으로 보여준다(handleHover 참고).
    // 좌측 pane 폭은 곧 타임라인 폭을 깎기 때문이다(2026-08-19 사용자 결정).
  ], [t, altHighlight, matchedResources, resourceFilter]);

  const toParamDate = (date) => (date ? format(date, PARAM_DATE_FORMAT) : null);

  const closeTooltip = useCallback(() => {
    clearTimeout(hoverTimerRef.current);
    tooltipTokenRef.current += 1;
    setTooltip(null);
  }, []);

  // old OnGanttTip — 바에 올리면 상세를 조회한다. old는 동기 XHR이라 hover마다 UI가 멈췄지만
  // new는 150ms 디바운스 + 비동기 + 요청 토큰으로 바꾼다(§E-6).
  const openTooltip = useCallback((box, clientX, clientY) => {
    const version = searchStateRef.current.versionState;
    const activity = box.activity;
    if (!version.versionCd || !activity) return;

    const token = ++tooltipTokenRef.current;

    fpResourceGanttService.getActivityDetail({
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCd: activity.plantCd,
      resourceCd: activity.resourceCd,
      activityId: activity.activityId
    })
      .then(response => {
        if (token !== tooltipTokenRef.current) return;

        const detail = response.data;
        if (!detail) return;

        // Tool 사용 정보 — 필요/가용 수량 비교로 초과/한계/정상 3단계(old tooltip의 toolSection).
        const tools = (activity.toolUsage ?? []).map(tool => {
          const used = tool.usedCnt ?? 0;
          const avail = tool.availCnt ?? 0;
          return {
            toolCd: tool.toolResourceCd ?? "",
            used,
            avail,
            status: used > avail ? "over" : used === avail ? "tight" : "ok"
          };
        });

        // **위계형(groups)으로 전환** — 주문 간트와 같은 구조다(2026-08-21 사용자 시안).
        // 항목·순서는 old 툴팁과 같고 위계만 3단으로 나눈다: 헤더(무엇) / 이 작업의 시각 / 상위 주문.
        //   ⚠️ 상세 조회는 코드와 명칭을 **각각** 내려준다(2026-08-25 백엔드 정리 — 그전에는 쿼리가
        //   "코드 (명칭)"으로 합쳐 같은 alias로 내렸다). 툴팁은 폭이 내용에 맞춰지므로 여기서는
        //   `formatCodeName`으로 한 줄로 합쳐 예전과 같은 표기를 유지한다(상세 패널은 두 줄로 나눈다).
        // 납기일 강조는 **바의 지연 플래그**(`box.late` = activity.lateYn)를 쓴다 — 상세 응답에는
        // 지연 여부 필드가 없고, 바 마크(우상단 삼각)와 같은 출처를 써야 화면 안에서 판정이 갈리지 않는다.
        setTooltip({
          x: clientX,
          y: clientY,
          kind: "activity",
          captionKey: "FP_ACTIVITY",
          code: formatCodeName(detail.itemCd, detail.itemNm),
          groups: [
            { rows: [{ labelKey: "FP_QTY", value: formatQty(detail.qty, detail.itemUom) }] },
            {
              rows: [
                { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.startTs) },
                { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.endTs) },
                { labelKey: "FP_TOTAL_WORK_TIME", value: formatTimeDiff(detail.tsDiff, t) }
              ]
            },
            {
              rows: [
                { labelKey: "FP_WO_CD", value: detail.woCd },
                { labelKey: "FP_INVENTORY_NM", value: formatCodeName(detail.woItemCd, detail.woItemNm), wrap: true },
                { labelKey: "FP_REQUEST_QTY", value: formatQty(detail.shptQty, detail.itemUom) },
                {
                  labelKey: "FP_DUE_DT",
                  value: dateUtils.formatDateTime(detail.dueDate),
                  ...(box.late ? { tone: "warn" } : null)
                }
              ]
            }
          ],
          tools
        });
      })
      .catch(e => debug(e));
  }, [t, viewName]);

  // 바 선택 — old OnGanttRunSelect: JC가 아닌 바를 고르면 그 공정의 대체자원 행을 좌측에서 강조한다.
  const selectBox = useCallback((box, row) => {
    const activity = box?.activity;
    const isJc = box ? box.type === "jc" : false;

    setSelectedId(box && !isJc ? box.id : null);
    setSelectedRowKey(box && !isJc ? (row?.rowKey ?? null) : null);

    const allowed = new Set();
    if (activity && !isJc) {
      (altResourcesRef.current?.[activity.routeCd] ?? []).forEach(alt => allowed.add(alt.resourceCd));
    }
    setAltHighlight(allowed);
  }, []);

  const debouncedRetrieveRef = useRef();
  if (!debouncedRetrieveRef.current) {
    debouncedRetrieveRef.current = debounce(() => retrieveResourceGantt(), 200);
  }
  useEffect(() => () => debouncedRetrieveRef.current.cancel(), []);

  // old: 전체 선택이면 필터 없음(빈 배열)으로 보낸다.
  const computeResourceCds = () => {
    const { selected: picked, options } = searchStateRef.current.resourceState;
    return picked.length === options.length ? [] : picked;
  };

  const retrieveResourceGantt = () => {
    const { versionState: version, periodState: period } = searchStateRef.current;

    if (!version.planScope || !version.versionCd) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_VERSION") });
      return;
    }
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLAN_PERIOD") });
      return;
    }

    const reqId = ++latestReqRef.current;
    const params = {
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCds: version.plantCd,
      resourceCds: computeResourceCds(),
      fromDt: toParamDate(period.startDate),
      toDt: toParamDate(period.endDate),
      usePlanPeriod: !!period.isEntirePlanPeriod
    };

    closeTooltip();
    selectBox(null, null);
    setEdges([]);
    setFocusWoCd(null);
    setLoading(true);

    // 의존선은 조회와 함께 미리 받아두고(old도 loadData에서 같이 호출), 실제 표시는 바를 더블클릭할
    // 때만 한다 — 전량 상시 표시는 비용이 크다(§5-4-(4)).
    fpResourceGanttService.getActivityRelations(params)
      .then(response => { if (reqId === latestReqRef.current) relationsRef.current = response.data ?? {}; })
      .catch(e => debug(e));

    // 하단 "생산 차질" 탭 — old loadShortage()와 같이 **간트 본조회와 같은 params로 함께** 받는다.
    // 활동 단위가 아니라 조회 범위 전체의 차질 목록이라 바 선택과 무관하다. 실패해도 간트 조회는
    // 계속 진행한다(보조 탭이 화면 전체를 막지 않게).
    setShortageRows([]);
    fpResourceGanttService.getShortageActivities(params)
      .then(response => { if (reqId === latestReqRef.current) setShortageRows(response.data ?? []); })
      .catch(e => debug(e));

    fpResourceGanttService.getActivities(params)
      .then(response => {
        if (reqId !== latestReqRef.current) return;

        const rows = (response.data ?? []).map((row, index) => ({
          rowKey: row.rowId ?? `${row.plantCd}-${row.resourceCd}-${index}`,
          plantCd: row.plantCd,
          plantNm: row.plantNm,
          resourceCd: row.resourceCd,
          resourceNm: row.resourceNm,
          downtimes: toRanges(row.downtimes),
          divideYDowntimes: toRanges(row.divideYDowntimes),
          boxes: buildLaneBoxes(row.activities)
        }));
        
        let count = 0;
        rows.forEach(row => count += row.boxes.length);
        console.log(count);

        setRawRows(rows);

        // old setGanttBound() + **조회 데이터 구간 합집합**(§E-4c) — 축이 데이터보다 짧아 바가
        // 잘리는 문제(§5-1a B8)를 막는다.
        const dataRange = rows.reduce((acc, row) => {
          row.boxes.forEach(box => {
            acc.start = acc.start == null ? box.s : Math.min(acc.start, box.s);
            acc.end = acc.end == null ? box.e : Math.max(acc.end, box.e);
          });
          return acc;
        }, { start: null, end: null });

        setBound(buildGanttBound({
          planStartTs: version.selectedVersion?.startTs,
          planEndTs: version.selectedVersion?.endTs,
          startDate: period.startDate,
          endDate: period.endDate,
          isEntirePlanPeriod: period.isEntirePlanPeriod,
          dataStartTs: dataRange.start,
          dataEndTs: dataRange.end
        }));

        // old setFrozenZone() — 계획 시작 ~ freeze 시각을 전 행 관통 밴드로 깐다.
        const freezeTs = toMs(version.selectedVersion?.freezeTs);
        const planStartTs = toMs(version.selectedVersion?.startTs);
        setFrozenZone(
          !Number.isNaN(freezeTs) && !Number.isNaN(planStartTs) && freezeTs > planStartTs
            ? { startTs: planStartTs, endTs: freezeTs }
            : null
        );

        if (rows.length === 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
      })
      .catch(e => {
        if (reqId === latestReqRef.current) setRawRows([]);
        debug(e);
      })
      .finally(() => {
        // 늦게 온 응답이 최신 조회의 로딩 표시를 끄지 않게 한다(요청 토큰).
        if (reqId === latestReqRef.current) setLoading(false);
      });
  };

  // ── 캔버스 렌더 ───────────────────────────────────────────────────────────
  // 바 세로 좌표 — OVERLAY는 행 전체를 쓰고, STACK은 트랙별로 나눠 쓴다.
  const barGeomY = useCallback((row, box, rowTop) => {
    if (overlapMode !== OVERLAP_MODE.STACK) {
      return { top: rowTop + LANE_BAR_PADDING_Y, height: Math.max(6, row.height - LANE_BAR_PADDING_Y * 2) };
    }

    const track = Math.min(box.track ?? 0, (row.trackCount ?? 1) - 1);
    return {
      top: rowTop + LANE_BAR_PADDING_Y + track * TRACK_HEIGHT,
      height: Math.max(6, TRACK_HEIGHT - LANE_BAR_PADDING_Y)
    };
  }, [overlapMode]);

  const handleDrawBackground = useCallback(({ ctx, x, width, height }) => {
    // 이 프레임의 바 좌표 인덱스를 새로 만든다(의존선이 뒤에서 읽는다).
    boxRectsRef.current = new Map();
    selectedRectRef.current = null;      // 선택 바가 화면 밖으로 스크롤되면 링도 같이 사라져야 한다
    selectedJcRectsRef.current = [];     // 같은 이유 + 매 프레임 새로 모아야 중복 누적이 없다
    if (!scale) return;

    // 배경 위계(§7-2a ②): frozen → 주말 → 휴무일 → 격자. 뒤에 그린 것이 위로 온다.
    // 주말은 요일 리듬(2.8%), 휴무일은 실제 비가동(사선)이라 휴무일이 위로 와야 한다.
    drawFrozenZone(ctx, { zone: frozenZone, x, top: 0, height });
    drawWeekends(ctx, { ranges: weekendRanges, x, top: 0, height });
    drawTimelineGrid(ctx, {
      ticks: ticks.bottom, subTicks: ticks.sub,
      x0: x(scale.t0), top: 0, height, viewLeft: 0, viewRight: width
    });
  }, [scale, ticks, weekendRanges, frozenZone]);

  const handleDrawRow = useCallback(({ ctx, x, row, rowTop, rowHeight: rh, width }) => {
    drawRowDivider(ctx, { left: 0, width, y: rowTop + rh });
    drawDowntimes(ctx, { downtimes: row.downtimes, x, rowTop, rowHeight: rh });

    // 라벨은 **분할 다운타임 마크보다 뒤에** 그린다(§7-20) — 마크가 바 위에 밝은 면을 얹으므로
    // 먼저 그리면 라벨이 함께 바랜다. 여기서는 좌표만 모아두고 마크 다음에 한 번에 그린다.
    const labelJobs = [];

    row.boxes.forEach(box => {
      const x0 = x(box.s);
      const x1 = box.type === "shtg" ? x0 : x(box.e);
      if (x1 < -40 || x0 > width + 40) return;      // 화면 밖은 그리지 않는다(가로 클리핑)

      const { top, height } = barGeomY(row, box, rowTop);
      const geom = { left: x0, width: Math.max(1, x1 - x0), top, height };

      // JC는 클릭 대상이 아니지만 **딸린 작업이 선택되면 선택 링이 같이 감싼다**(§7-18).
      // 띠 자체의 모양은 선택 여부와 무관하다 — `drawBox`에 알릴 것이 없고 좌표만 모은다(§7-18a).
      const isSelectedJc = box.type === "jc" && !!selectedId && jcParentIdOf(box.id) === selectedId;

      // **0건이면 흐리기를 걸지 않는다**(§7-8) — 일치가 없는데 전 화면을 흐리게 만들면 "정보 0 +
      // 고장난 것 같은 화면"이 된다. 0건 사실은 검색 pill의 붉은 `0` 배지가 알린다.
      const dim = dimming && !matchFn(box.activity);

      const rect = drawBox(ctx, { box, geom, dim, hovered: hoverId === box.id, withLabel: false });

      // 라벨은 작업 바에만 있다 — JC 띠(라벨 없음)와 결품 마름모(rect가 null)는 제외한다.
      if (box.type === "box" && rect) labelJobs.push({ box, rect, dim });

      // 선택 링은 **모든 행을 그린 뒤** overlay에서 그린다 — 여기서 그리면 바 간격이 1px이라 다음
      // 이웃 바가 링을 덮는다(§7-9). 여기서는 좌표만 기억한다.
      if (selectedId && box.id === selectedId) selectedRectRef.current = rect;
      if (isSelectedJc && rect) selectedJcRectsRef.current.push(rect);

      if (box.id) boxRectsRef.current.set(box.id, { x0: geom.left, x1: geom.left + geom.width, cy: top + height / 2 });
    });

    // 분할 다운타임 마크는 트랙 0 높이에 맞춰 얹는다(old도 바 위 단일 밴드).
    const { top, height } = barGeomY(row, { track: 0 }, rowTop);
    drawDivideMarks(ctx, {
      marks: row.divideYDowntimes, boxes: row.boxes, x, barTop: top, barHeight: height, searchActive: dimming, matchFn
    });

    // 마지막에 라벨 — 마크의 밝은 면 위로 올라온다(§7-20).
    labelJobs.forEach(job => drawBoxLabel(ctx, job));
  }, [barGeomY, dimming, matchFn, selectedId, hoverId]);

  const handleDrawOverlay = useCallback(({ ctx, x, scrollTop, height }) => {
    // 선택 링부터 — 의존선(더블클릭으로 요청해 띄운 경로)이 링 위를 지나가는 편이 자연스럽다.
    drawSelectionRing(ctx, { rect: selectionRect(selectedRectRef.current, selectedJcRectsRef.current) });

    // 끝점 좌표 해석 — **그려진 사각형이 있으면 그걸 쓰고**(정수 정렬·틈까지 반영된 정확한 값),
    // 없으면(행 가상화로 화면 밖) **레이아웃에서 계산한다**(§7-12).
    // 이전에는 그려진 사각형만 썼기 때문에 한쪽 끝 행이 뷰포트를 벗어나는 순간 선이 사라졌다.
    // 계산이 정확한 이유는 **행 위치를 우리가 소유**하기 때문이다(§7-1) — AG Grid 시절에는 그리드가
    // 소유해서 이 계산을 신뢰할 수 없었고(B4·B13~B15) 그래서 "그려진 것만" 쓰는 규칙이 생겼다.
    const resolve = (id) => {
      const drawn = boxRectsRef.current.get(id);
      if (drawn) return drawn;

      const hit = boxIndex.get(id);
      if (!hit) return null;

      const geom = barGeomY(hit.row, hit.box, hit.row.top - scrollTop);
      const x0 = x(hit.box.s);
      return {
        x0,
        x1: hit.box.type === "shtg" ? x0 : x(hit.box.e),
        cy: geom.top + geom.height / 2
      };
    };

    edges.forEach(({ from, to }) => {
      const source = resolve(from);
      const target = resolve(to);
      if (!source || !target) return;
      // 양쪽 모두 뷰포트 위/아래로 한참 벗어난 간선은 그리지 않는다 — 캔버스가 알아서 잘라내지만
      // 검색으로 여러 작업주문이 켜지면 화면 밖 곡선이 수백 개가 되어 스크롤이 눌린다.
      if ((source.cy < -80 && target.cy < -80) || (source.cy > height + 80 && target.cy > height + 80)) return;

      // `focus` — 이 화면의 의존선은 더블클릭으로 **요청해서 띄운** 경로다. 배경(고채도 바 수백 개)
      // 사이를 여러 행 관통하므로 주문 간트의 ambient 톤으로는 묻힌다(§7-2j).
      drawDependency(ctx, { sx: source.x1, sy: source.cy, tx: target.x0, ty: target.cy, emphasis: "focus" });
    });
  }, [edges, boxIndex, barGeomY]);

  // ── 입력 ──────────────────────────────────────────────────────────────────
  // 뒤에서부터 훑어 "나중에 시작한(= 위에 그려진)" 박스를 우선 잡는다. OVERLAY 모드에서 겹친 바를
  // 고를 때 보이는 것과 잡히는 것이 일치해야 한다.
  const findBoxAt = useCallback((row, ts) => {
    if (!row?.boxes?.length || ts == null) return null;

    for (let i = row.boxes.length - 1; i >= 0; i -= 1) {
      const box = row.boxes[i];
      const to = box.type === "shtg" ? box.s : box.e;
      if (ts >= box.s && ts <= to) return box;
    }
    return null;
  }, []);

  // ── 우클릭 → 메뉴 → 작업 상세 정보 ──────────────────────────────────────
  // 값 포맷은 툴팁과 같은 유틸을 쓴다(`formatQty`/`formatTimeDiff`/`dateUtils`) — 두 곳이 갈라지면
  // 같은 작업이 다른 값처럼 보인다. 행 렌더도 `GanttDetailRows`를 공유한다(§7-7).
  const loadDetail = useCallback((box) => {
    const version = searchStateRef.current.versionState;
    const activity = box?.activity;
    if (!version.versionCd || !activity) return;

    const token = ++detailTokenRef.current;
    setDetailLoading(true);

    fpResourceGanttService.getActivityDetail({
      viewId: viewName,
      subId: "",
      planScope: version.planScope,
      versionCd: version.versionCd,
      plantCd: activity.plantCd,
      resourceCd: activity.resourceCd,
      activityId: activity.activityId
    })
      .then(response => {
        // 바를 빠르게 옮겨 다닐 때 늦게 온 응답이 화면을 덮지 않게 한다(툴팁과 같은 토큰 패턴).
        // **로딩 해제도 토큰 검사 뒤에** 한다 — 뒤처진 응답이 진행 중인 조회의 진행 바를 끄면 안 된다.
        if (token !== detailTokenRef.current) return;
        setDetailLoading(false);

        const detail = response.data;
        if (!detail) return;

        // `ActivityDetailDto` 하나에 **작업 + 주문 필드가 다 들어 있다** → API 한 번으로 두 섹션을 채운다.
        // 코드/명칭은 백엔드가 **각각** 내려준다(2026-08-25 정리 — 그전에는 쿼리에서 "코드 (명칭)"으로
        // 합쳐 보내 폭이 고정된 이 패널에서 아무 데서나 접혔다). `subValue`로 넘기면 "코드 굵게 /
        // 명칭 작게" 두 줄 고정으로 그려진다.
        setDetailSections([
          {
            key: "activity",
            titleKey: "FP_ACTIVITY_INFO",
            rows: [
              { labelKey: "FP_PRODUCTION_ITEM", value: detail.itemCd, subValue: detail.itemNm },
              { labelKey: "FP_QTY", value: formatQty(detail.qty, detail.itemUom) },
              { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.startTs) },
              { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.endTs) },
              { labelKey: "FP_TOTAL_WORK_TIME", value: formatTimeDiff(detail.tsDiff, t) },
              { labelKey: "FP_RESOURCE", value: detail.resourceCd, subValue: detail.resourceNm },
              { labelKey: "FP_ROUTE", value: detail.routeCd, subValue: detail.routeNm },
              { labelKey: "FP_WIP", value: Boolean(detail.wipYn), checkbox: true }
            ]
          },
          {
            key: "order",
            titleKey: "FP_ORDER_INFO",
            rows: [
              { labelKey: "FP_WORK_ORDER_CODE", value: detail.woCd },
              { labelKey: "FP_INVENTORY_NM", value: detail.woItemCd, subValue: detail.woItemNm },
              { labelKey: "FP_REQUEST_QTY", value: formatQty(detail.shptQty, detail.itemUom) },
              { labelKey: "FP_START_TS", value: dateUtils.formatDateTime(detail.woStartTs) },
              { labelKey: "FP_END_TS", value: dateUtils.formatDateTime(detail.woEndTs) },
              { labelKey: "FP_TOTAL_PRODUCTION_TIME", value: formatTimeDiff(detail.woTsDiff, t) },
              {
                labelKey: "FP_DUE_DT",
                value: dateUtils.formatDateTime(detail.dueDate),
                ...(detail.lateYn ? { tone: "warn" } : null)
              },
              {
                // 상태 낱말이라 뱃지(둥근 배경)로 — 위 납기일은 값 자체가 데이터라 글자색만 바꾼다.
                labelKey: "FP_ONTIME_DELIVERY",
                value: t(detail.lateYn ? "FP_DELAY" : "FP_ONTIME"),
                tone: detail.lateYn ? "warn" : "ok",
                chip: true
              },
              { labelKey: "FP_PLAN_SEQ", value: detail.planSeq == null ? "" : numberUtil.formatNumber(Number(detail.planSeq)) },
              { labelKey: "FP_SO_CD", value: detail.dmndCd },
              { labelKey: "FP_SALES_ORDER_QTY", value: formatQty(detail.dmndQty, detail.itemUom) },
              { labelKey: "customer", value: detail.customerCd, subValue: detail.customerNm }
            ]
          }
        ]);
      })
      .catch(e => {
        if (token === detailTokenRef.current) setDetailLoading(false);
        debug(e);
      });
  }, [t, viewName]);

  // 프레임은 박스를 모른다 — 여기서 `ts`로 찾는다. 준비시간(JC)이면 메뉴를 열지 않는다(old 규칙).
  const handleContextPoint = useCallback((row, { ts, clientX, clientY }) => {
    const box = findBoxAt(row, ts);
    if (!box || box.type === "jc") return;

    closeTooltip();                       // 메뉴가 뜨는 동안 툴팁이 겹치지 않게
    selectBox(box, row);                  // 우클릭도 선택으로 본다 — 어느 바의 상세인지 남아야 한다
    setMenuTarget({ box, row });
    setMenuAnchor({ x: clientX, y: clientY });
  }, [findBoxAt, closeTooltip, selectBox]);


  const handleRowActivate = useCallback((row, { ts }) => {
    // old OnClick: 싱글 클릭은 선택만 하고 의존선은 지운다(검색 중이면 검색 의존선은 유지).
    const box = findBoxAt(row, ts);
    selectBox(box, row);
    // 포커스를 푸는 통로는 **싱글 클릭**이다(별도 해제 버튼을 두지 않는다 — 의존선을 지우는 동작과
    // 같은 제스처라 배울 것이 없다). 검색 중이었다면 검색 강조·의존선으로 되돌아간다.
    setFocusWoCd(null);
    if (!searchActive) setEdges([]);
    else if (focusWoCd) setEdges(buildSearchEdges(search));

    // 패널이 열려 있으면 그 바의 상세로 갱신한다(JC는 제외 — 선택 대상이 아니다).
    if (detailOpen && box && box.type !== "jc") loadDetail(box);
  }, [findBoxAt, selectBox, searchActive, focusWoCd, search, buildSearchEdges, detailOpen, loadDetail]);

  // 더블클릭 = 그 작업주문의 의존선 표시. old는 TreeGrid가 클릭 이벤트를 하나만 줘서 400ms 수동
  // 판정을 했지만, 여기서는 네이티브 dblclick으로 정확히 갈린다(§5-2a D7).
  const handleRowDoubleActivate = useCallback((row, { ts }) => {
    const box = findBoxAt(row, ts);
    if (!box || box.type === "jc") return;

    selectBox(box, row);

    // woCd 가 없는 바(결품 마름모 등)는 의존선도 포커스도 걸 대상이 없다 — 흐리기만 걸리면
    // "전 화면이 꺼졌는데 강조된 것은 하나도 없는" 상태가 된다.
    const woCd = box.activity?.woCd ?? null;
    setFocusWoCd(woCd);
    setEdges(woCd ? parseRelations(relationsRef.current?.[woCd]) : []);
  }, [findBoxAt, selectBox]);

  const handleHover = useCallback((row, { ts, clientX, clientY, inLeftPane, colKey }) => {
    // 좌측 pane — old `OnTip`(`col === 'resourceCd'` → `row.resourceNm`) 복원. 자원명은 이미 행에
    // 있어서 서버 호출이 없다 → 바 툴팁과 달리 **지연 없이 즉시** 띄운다.
    // 같은 셀 위에서는 갱신하지 않는다(매 mousemove마다 setState하면 툴팁이 커서를 따라 떨린다).
    if (inLeftPane) {
      const name = colKey === "resourceCd" ? (row?.resourceNm ?? "") : "";
      const key = name ? row.rowKey : null;
      if (leftTipKeyRef.current === key) return;

      leftTipKeyRef.current = key;
      setHoverId(null);
      closeTooltip();
      if (name) setTooltip({ x: clientX, y: clientY, text: name });
      return;
    }
    leftTipKeyRef.current = null;

    // 상세 패널이 열려 있으면 바 툴팁은 띄우지 않는다 — 같은 정보가 두 곳에 뜨면 시선이 분산된다(§7-7).
    if (detailOpen) {
      setHoverId(null);
      closeTooltip();
      return;
    }

    const box = row ? findBoxAt(row, ts) : null;

    if (!box || box.type === "jc") {
      setHoverId(null);
      closeTooltip();
      return;
    }
    if (hoverId === box.id) return;

    closeTooltip();
    setHoverId(box.id);                 // hover 음영은 즉시, 툴팁만 150ms 지연
    hoverTimerRef.current = setTimeout(() => openTooltip(box, clientX, clientY), 150);
  }, [findBoxAt, hoverId, closeTooltip, openTooltip, detailOpen]);

  const closeMenu = useCallback(() => setMenuAnchor(null), []);

  const openDetail = useCallback(() => {
    setMenuAnchor(null);
    if (!menuTarget?.box) return;

    setDetailSections([]);                // 이전 바의 값이 남아 보이지 않게 비우고 시작
    setDetailOpen(true);
    loadDetail(menuTarget.box);
  }, [menuTarget, loadDetail]);

  const toggleDetailSection = useCallback((key) => {
    setOpenDetailSections(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleWheelZoom = useCallback((delta) => {
    setWidthZoom(prev => {
      const factor = delta < 0 ? WIDTH_ZOOM_STEP : 1 / WIDTH_ZOOM_STEP;
      return Math.min(WIDTH_ZOOM_MAX, Math.max(WIDTH_ZOOM_MIN, prev * factor));
    });
  }, []);

  // 활동 검색(주문/품목) 커밋 — 서버 재조회가 아니라 강조/흐리기만 바꾼다. 검색이 걸리면 old처럼
  // 일치하는 작업주문의 의존선을 한꺼번에 표시한다(old setDependencies).
  const applyActivitySearch = useCallback((committed) => {
    const next = { order: committed?.order ?? "", item: committed?.item ?? "" };
    setSearch(next);
    setMatchIndex(0);
    selectBox(null, null);
    setFocusWoCd(null);                   // 검색을 새로 걸면 이전 포커스는 의미가 없다
    setEdges(buildSearchEdges(next));
  }, [buildSearchEdges, selectBox]);

  // ⌃⌄ — 일치 작업 사이 이동(브라우저 찾기와 같은 정신 모델). 이동한 작업을 **선택 상태로** 만든다:
  // 별도 강조 링을 새로 만들지 않고 기존 선택 표시를 재사용하는 것이고, 부수로 대체자원 강조와
  // (열려 있으면) 상세 패널도 그 작업으로 따라온다.
  const stepMatch = useCallback((delta) => {
    if (matches.length === 0) return;

    const nextIndex = (matchIndex + delta + matches.length) % matches.length;
    setMatchIndex(nextIndex);

    const { box, row } = matches[nextIndex];
    selectBox(box, row);

    // 화면 중앙에 두려면 뷰포트 폭이 필요한데 프레임이 노출하지 않는다 — **왼쪽에서 약간 안쪽**에
    // 놓는 방식으로 충분하다(찾은 위치와 그 뒤 공정이 함께 보인다).
    const left = scale ? Math.max(0, scale.x(box.s) - 240) : 0;
    frameApiRef.current?.scrollTo({ left, top: Math.max(0, row.top - row.height) });
  }, [matches, matchIndex, scale, selectBox]);

  // ── 조회조건 연동 ─────────────────────────────────────────────────────────
  // 자원 멀티셀렉트 옵션 — old loadResources(planScope 변경 시 재조회 + 전체 선택으로 초기화).
  useEffect(() => {
    setResourceDraft(null);
    if (!versionState.planScope) {
      setResourceState({ selected: [], options: [] });
      return undefined;
    }

    let cancelled = false;
    const reqId = ++latestResourceReqRef.current;

    fpCommonService.getResources({ viewId: viewName, subId: "", planScope: versionState.planScope, resourceCdNm: "" })
      .then(response => {
        if (cancelled || reqId !== latestResourceReqRef.current) return;
        const options = (response.data ?? []).map(r => ({ value: r.resourceCd, label: r.resourceNm ?? r.resourceCd }));
        setResourceState({ selected: options.map(o => o.value), options });
      })
      .catch(e => debug(e));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.planScope]);

  // ── 자원 멀티셀렉트(50~100개 규모) ───────────────────────────────────────────
  // old는 InputField(type="multiSelect") 하나에 전량을 나열해서, 자원이 많으면 목록에서 찾기가
  // 불편했다. new에서 같은 문제를 다루는 선례는 두 계열인데(조사 결과 — 아래 근거),
  // **이 화면은 옵션이 50~100개라 "검색 가능한 다중선택" 계열로 충분하다**:
  //   - `components/filter/ItemAutocomplete.js` — 서버 검색형(400ms 디바운스 + 검색 API). 목록을
  //     아예 내리지 않는 방식으로, 품목처럼 수천~무제한일 때 쓴다. **여기엔 과하다**(자원은 planScope
  //     단위로 전량을 이미 한 번에 받아오는 게 old·new 공통 전제고, 그 응답이 100건 수준이다).
  //   - `components/filter/ResourceFilter.js`(`isMultiSelect`) — MP 자원 간트가 쓰는 체크박스
  //     Popover. **검색창·전체선택이 없어서 old와 같은 불편이 그대로 남고**, DP/MP 도메인 API
  //     (`services/data/resource-service`)+전역 redux 캐시에 묶여 FP(planScope 기준)에서 재사용 불가.
  //     공용 컴포넌트라 수정도 금지 대상이다.
  // 그래서 MUI Autocomplete(내장 텍스트 필터)를 유지하고 대량 선택에 필요한 것만 얹었다:
  // 요약 표시(전체/‘첫 항목 +N’) · 체크박스 · 모두 선택/전체 해제 · 닫을 때 커밋.
  // 가상화(react-window)는 의존성 추가 금지라 선택지가 아니고, 이 규모에선 필요도 없다.
  const selectedResourceOptions = useMemo(
    () => {
      if (resourceDraft) return resourceDraft;
      const picked = new Set(resourceState.selected);
      return resourceState.options.filter(option => picked.has(option.value));
    },
    [resourceDraft, resourceState.options, resourceState.selected]
  );

  const allResourcesSelected = resourceState.options.length > 0
    && selectedResourceOptions.length === resourceState.options.length;

  // 닫을 때 커밋 — **내용이 같으면 커밋하지 않는다.** 자동 조회 effect가 `selected`를 의존성으로
  // 보므로, 내용이 같아도 새 배열을 넣으면 식별자가 바뀌어 불필요한 재조회가 나간다.
  // 선택 순서로 들어온 draft를 옵션 순서로 정규화해서 그 비교가 성립하게 만든다.
  const commitResourceSelection = useCallback(() => {
    setResourceDraft(null);
    if (!resourceDraft) return;

    const picked = new Set(resourceDraft.map(option => option.value));
    setResourceState(prev => {
      const next = prev.options.filter(option => picked.has(option.value)).map(option => option.value);
      const same = next.length === prev.selected.length && next.every((cd, i) => cd === prev.selected[i]);
      return same ? prev : { ...prev, selected: next };
    });
  }, [resourceDraft]);

  // 드롭다운 상단 고정 액션. `onMouseDown` 기본동작을 막지 않으면 버튼을 누르는 순간 입력이
  // 포커스를 잃어 드롭다운이 닫힌다.
  const renderResourcePaper = useCallback(({ children, ...paperProps }) => (
    <Paper {...paperProps}>
      <Box
        sx={{ display: "flex", gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}
        onMouseDown={(event) => event.preventDefault()}
      >
        <Button size="small" onClick={() => setResourceDraft(resourceState.options)}>{t("selectAll")}</Button>
        <Button size="small" color="inherit" onClick={() => setResourceDraft([])}>{t("clearAll")}</Button>
      </Box>
      {children}
    </Paper>
  ), [resourceState.options, t]);

  // 대체자원 목록 — old는 조회 직후 화면에 있는 자원 전체분을 한 번에 받아둔다.
  useEffect(() => {
    const planScope = versionState.planScope;
    const resourceCds = rawRows.map(row => row.resourceCd).filter(Boolean);
    if (!planScope || resourceCds.length === 0) {
      altResourcesRef.current = {};
      return;
    }

    fpResourceGanttService.getAltResources({ viewId: viewName, subId: "", planScope, resourceCds })
      .then(response => { altResourcesRef.current = response.data ?? {}; })
      .catch(e => debug(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawRows, versionState.planScope]);

  useEffect(() => {
    const version = versionState.selectedVersion;
    if (!version?.startTs) {
      setPeriodState(prev => (prev.startDate || prev.endDate) ? { ...prev, startDate: null, endDate: null } : prev);
      return;
    }

    const startDate = new Date(version.startTs);
    setPeriodState(prev => ({
      ...prev, startDate, endDate: new Date(startDate.getTime() + SEARCH_ZONE_DAYS * 24 * 60 * 60 * 1000)
    }));
    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.selectedVersion]);

  useEffect(() => {
    if (versionState.versionCd) return;

    debouncedRetrieveRef.current.cancel();
    setRawRows([]);
    setEdges([]);
    setFocusWoCd(null);
    setBound(null);
    setFrozenZone(null);
    setLoading(false);
    closeTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd]);

  // 선택형 조건(버전/plant/기간/자원)이 바뀌면 값이 준비되는 즉시 자동 조회(§A-9b).
  // 활동 검색(주문/품목)은 서버 조건이 아니라 화면 필터라 여기 포함되지 않는다.
  useEffect(() => {
    const version = searchStateRef.current.versionState;
    const period = searchStateRef.current.periodState;
    if (!version.planScope || !version.versionCd) return;
    if (!period.isEntirePlanPeriod && (!period.startDate || !period.endDate)) return;

    debouncedRetrieveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodState.startDate, periodState.endDate, periodState.isEntirePlanPeriod, versionState.plantCd, resourceState.selected]);

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);

  return (
    <>
      {/* 화면 루트를 flex column으로 둔다 — 조회조건(FilterContainer) 높이를 자동으로 빼고 남는
          만큼만 간트가 차지한다. 이전에는 간트 래퍼가 `height: calc(100% - 8px)`였는데, flex column
          안에서 그 값은 **조회조건 높이를 빼주지 않아** 합계가 부모를 넘겼다. 그만큼 프레임 하단이
          잘려 **가로 스크롤바가 화면 밖으로 밀려나** thumb를 잡을 수 없었다(2026-08-19 사용자 제보:
          "트랙 클릭은 되는데 드래그만 안 된다" — 남은 조각에만 클릭이 닿았던 것). */}
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
        {/* `hasIcons={false}` — 표시 배율 묶음을 아래 툴바 줄로 내려서 **우측에 밀어 붙일 것이 없다.**
            안 주면 `FilterContainer`가 **마지막 자식**(자원 멀티셀렉트)을 우측 정렬해 조회조건 줄이
            좌우로 갈라진다(`FilterContainer.js:37-42`). `false`면 빈 자식 하나를 뒤에 덧대므로
            조회조건은 전부 왼쪽에 남고 **접기 버튼은 그대로 우측에 렌더된다**. */}
        <FilterContainer hasIcons={false}>
          <VersionSearchGroup
            value={versionState}
            onChange={(partial) => setVersionState(prev => ({ ...prev, ...partial }))}
          />
          {/* old useBucket={false} — 활동(activity) 단위 화면이라 버킷 개념이 없다. */}
          <PeriodSearchGroup
            value={periodState}
            onChange={(partial) => setPeriodState(prev => ({ ...prev, ...partial }))}
            useBucket={false}
          />
          {/* old InputField(type="multiSelect", resourceCd) — 검색 가능한 다중선택으로 재구성.
              설계 근거와 다른 후보(서버 검색형/체크박스 Popover)를 왜 안 썼는지는 위 핸들러 주석 참고. */}
          <Autocomplete
            multiple
            size="small"
            disableCloseOnSelect
            disableClearable
            options={resourceState.options}
            getOptionLabel={(o) => o?.label ?? ""}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={selectedResourceOptions}
            onChange={(_, options) => setResourceDraft(options)}
            onClose={commitResourceSelection}
            PaperComponent={renderResourcePaper}
            // 선택이 100개까지 가므로 태그(Chip)를 쌓지 않고 한 줄 요약만 보여준다 — 기본값이
            // "전체 선택"이라 Chip으로 두면 조회조건 줄이 통째로 태그밭이 된다.
            renderTags={(value) => value.length === 0 ? null : (
              <Box component="span" sx={{ pl: 0.75, fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {allResourcesSelected
                  ? t("ALL")
                  : value.length === 1 ? value[0].label : `${value[0].label} +${value.length - 1}`}
              </Box>
            )}
            renderOption={(props, option, { selected }) => (
              <Box component="li" {...props} key={option.value} sx={{ gap: 0.5 }}>
                <Checkbox size="small" checked={selected} sx={{ p: 0.25 }} />
                {option.label}
              </Box>
            )}
            renderInput={(params) => <TextField {...params} label={t("FP_RESOURCE")} />}
            sx={{ ml: 1.5, minWidth: 250 }}
          />
        </FilterContainer>

        {/* ── 툴바 한 줄 — 표시 배율 묶음을 **조회조건보다 한 단 아래**로 내린다
            (2026-08-24 사용자 지시, 보정 간트와 통일 §7-27).
            조회조건(무엇을 가져올까)과 표시 배율(어떻게 볼까)은 성격이 다르고, 한 줄에 섞여 있으면
            눈이 두 번 움직인다. 보정 간트(`simulation/AdjustmentGantt.js`)가 이미 이 구조라
            **세 간트의 줄 구성이 같아진다** — 그쪽은 좌측에 보정 조작 묶음이 하나 더 붙을 뿐이다.
            자원 간트는 좌측에 놓을 조작이 없어 `ml: "auto"` 하나로 우측 정렬한다. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
            <GanttViewControls
              t={t}
              /* 작업 검색 — **조회조건이 아니라 화면 내 강조 필터**라 표시 배율 묶음 소속이다
                 (§7-8, 버킷을 옮긴 것과 같은 논리 §7-5). old도 간트 툴바의 pill이었다. */
              startSlot={(
                <GanttActivitySearch
                  t={t}
                  value={search}
                  fields={SEARCH_FIELDS}
                  onCommit={applyActivitySearch}
                  matchCount={matches.length}
                  matchIndex={matchIndex}
                  onStep={stepMatch}
                />
              )}
              headerUnit={headerUnit}
              onHeaderUnitChange={setHeaderUnit}
              widthZoom={widthZoom}
              onWidthZoomChange={setWidthZoom}
              rowHeight={rowHeight}
              onRowHeightChange={setRowHeight}
              /* 이 줄엔 조회조건 접기 버튼이 없어 기본 우측 여백(`mr`)이 끝에 뜨는 틈이 된다. */
              sx={{ mr: 0 }}
            />
          </Box>
        </Box>

        {/* ── 상단 영역: 간트 (§7-14) ───────────────────────────────────────────
            ⚠️ 반드시 `flex: 1 1 0` + `minHeight: 0`이다. 퍼센트나 `calc` 높이를 주면 형제(분할 바 +
            하단 영역) 높이를 빼주지 못해 합계가 부모를 넘기고, 그만큼 프레임 하단이 잘려
            **가로 스크롤바가 화면 밖으로 밀려나 thumb를 잡을 수 없게 된다** — 위 조회조건 관련
            주석과 같은 함정을 2026-08-19에 이미 한 번 겪었다. 가로 스크롤바는 `GanttFrame` 내부의
            전용 스트립이라(GanttFrame.js:35-45) 하단 영역과는 분리되어 그대로 보인다. */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 0", minHeight: 0, mt: 1 }}>
          {/* 상세 패널이 이 박스 안에 절대배치되므로 `position: relative`가 필요하다.
              **오버레이라 간트 폭이 줄지 않는다** — persistent Drawer로 밀면 캔버스가 리사이즈되며
              스케일·눈금이 재계산돼 보고 있던 바가 움직인다(§7-7 A안 선정 근거). */}
          <Box sx={{ position: "relative", flex: "1 1 0", minHeight: 0, border: "1px solid", borderColor: "divider" }}>
          <GanttFrame
            rows={layout.rows}
            totalHeight={layout.totalHeight}
            columns={columns}
            scale={scale}
            ticks={ticks}
            selectedRowKey={selectedRowKey}
            loading={loading}
            loadingMessage={t("MSG_Loading")}
            drawBackground={handleDrawBackground}
            drawRow={handleDrawRow}
            drawOverlay={handleDrawOverlay}
            onRowActivate={handleRowActivate}
            onRowDoubleActivate={handleRowDoubleActivate}
            onHoverPoint={handleHover}
            onContextPoint={handleContextPoint}
            onWheelZoom={handleWheelZoom}
            onColumnFilterClick={(column, anchorEl) => setFilterAnchor(anchorEl)}
            filterTitle={t("filter")}
            apiRef={frameApiRef}
          />

          <GanttDetailPanel
            open={detailOpen}
            sections={detailSections}
            openSections={openDetailSections}
            onToggleSection={toggleDetailSection}
            onClose={() => setDetailOpen(false)}
            loading={detailLoading}
            loadingMessage={t("MSG_Loading")}
            t={t}
          />
          </Box>
        </Box>

        {/* 분할 바 — 접혀 있어도 남겨 둔다(드래그로 바로 열 수 있게). 두께·색은 공용
            `SplitterContainer`의 기본값(10px, #F5F5F5)에 맞춰 다른 FP Report 화면과 같아 보이게 한다.
            위(간트 테두리)·아래(탭 바)와 각각 4px 띄운다 — 붙여 두면 회색 띠가 간트 테두리에
            들러붙어 프레임의 일부처럼 보인다(2026-08-24 사용자 요청). 여백은 flex 아이템의 외곽
            크기에 포함되므로 상단 영역이 그만큼 줄어들 뿐, 드래그 계산(clientY 델타)에는 영향이 없다. */}
        <Box
          onMouseDown={handleDetailDragStart}
          sx={{
            flex: `0 0 ${DETAIL_GUTTER}px`, my: `${DETAIL_GUTTER_MARGIN}px`,
            backgroundColor: "#F5F5F5", cursor: "row-resize",
            display: "flex", alignItems: "center", justifyContent: "center",
            "&::after": {
              content: '""', width: 24, height: 2, borderTop: "1px dotted", borderBottom: "1px dotted",
              borderColor: "grey.500"
            }
          }}
        />

        {/* 하단 영역 = 탭 바 + 선택한 탭의 그리드. **탭 바가 분할 바 아래(이 영역 안)에 있다** —
            old `DetailsSplitArea`의 구성 그대로다. 접힘 = 이 영역이 탭 바 높이만 남는 상태이므로
            old의 초기 97/3(화살표 + 탭 라벨만 보이는 띠)과 같은 모양이 된다. */}
        <Box sx={{ display: "flex", flexDirection: "column", flex: `0 0 ${detailHeight}px`, minHeight: 0, overflow: "hidden" }}>
          {/* 탭 바 — 화살표(접기/펼치기)를 왼쪽에 붙인다(old `tabStartAdornment`와 같은 위치).
              화살표 모양은 `detailCollapsed`로 결정되므로 **드래그로 줄여도 자동으로 따라온다**
              (old handleSplitDragEnd의 임계 판정을 그대로 옮긴 것이다). */}
          <Box sx={{ display: "flex", alignItems: "center", flex: `0 0 ${DETAIL_TAB_BAR_HEIGHT}px` }}>
            <Tooltip title={detailCollapsed ? t("FP_EXPAND") : t("FP_COLLAPSE")}>
              <IconButton size="small" onClick={toggleDetail} sx={{ mx: 0.5 }}>
                {detailCollapsed ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Tabs
              value={detailTab}
              onChange={(_, value) => setDetailTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT }}
            >
              {DETAIL_TABS.map(tab => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={t(tab.labelKey)}
                  sx={{ minHeight: DETAIL_TAB_BAR_HEIGHT, py: 0.75, fontSize: "0.875rem" }}
                />
              ))}
            </Tabs>
          </Box>

          {/* 탭 내용 — 접혀 있으면(탭 바 높이만 남음) 렌더하지 않는다.
              **활성 탭 하나만 마운트한다** — 3개를 항상 마운트해두면 접힌 동안 AG Grid가 0px로
              초기화돼 펼칠 때 헤더가 그려지지 않는 문제가 있다. 대신 탭을 왕복하면 그리드가 다시
              만들어지므로 스크롤 위치는 유지되지 않는다(조회 전용 보조 그리드라 감수한다). */}
          <Box
            sx={{
              flex: "1 1 0", minHeight: 0, overflow: "hidden",
              pt: detailCollapsed ? 0 : `${DETAIL_CONTENT_GAP}px`
            }}
          >
            {!detailCollapsed ? (
              <>
                {detailTab === "plan" && (
                  <ResourcePlanDetailTab rows={resourcePlanRows} viewName={`${viewName}_PLAN_DETAIL`} t={t} />
                )}
                {detailTab === "shortage" && (
                  <ShortageDetailTab rows={shortageRows} viewName={`${viewName}_SHORTAGE`} t={t} />
                )}
                {detailTab === "psi" && (
                  <PsiTab
                    active
                    activity={selectedActivity}
                    versionState={versionState}
                    viewName={`${viewName}_PSI`}
                    t={t}
                  />
                )}
              </>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* 우클릭 컨텍스트 메뉴 — 커서 위치를 가상 앵커로 쓴다(툴팁과 같은 기법).
          항목은 old와 같이 1개다. 메뉴 프레임이 생겼으니 확장은 여기에 추가하면 된다. */}
      <Menu
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorReference="anchorPosition"
        anchorPosition={menuAnchor ? { top: menuAnchor.y, left: menuAnchor.x } : undefined}
      >
        <MenuItem onClick={openDetail}>{t("FP_ACTIVITY_DETAIL")}</MenuItem>
      </Menu>

      {/* 자원 컬럼 값 필터 — 트리거(깔때기)는 `GanttFrame` 헤더가, 팝오버는 화면이 띄운다(§7-13) */}
      <GanttColumnFilter
        t={t}
        anchorEl={filterAnchor}
        label={t("FP_RESOURCE")}
        options={resourceFilterOptions}
        value={resourceFilter}
        onCommit={setResourceFilter}
        onClose={() => setFilterAnchor(null)}
      />

      {/* 바 hover 상세(old OnGanttTip) — 항목/내용 2열 정렬은 공용 컴포넌트가 갖는다 */}
      <GanttTooltip tooltip={tooltip} t={t} />

      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ResourceGantt);
